<script setup>
import { Icon } from "@iconify/vue";
import { ethers } from "ethers";
import { dispatchTxAlert, resolveTransactionHash } from "@/utils/helpers";
import { logFirebaseEvent } from "@/plugins/firebase";

import BattlePoolABI from "@data/BattlePoolABI.json";

const emits = defineEmits(["onConfirm", "onCancel"]);

const props = defineProps({
  userInfo: {
    type: Object,
    default: () => ({}),
  },
});

const userStore = useUserStore();
const CONTRACT_ADDRESS = import.meta.env.VITE_CONTRACT_BATTLE_POOL;

const tradeSigilsDialog = ref(false);
const loading = ref(false);
const totalOwnerKeysBought = ref(0);
const sliderAmount = ref(1);
const maxAmount = ref(10);
const buyLoading = ref(false);
const buyCost = ref("0.0");
const buyError = ref(null);
const sellLoading = ref(false);
const sellCost = ref("0.0");
const sellError = ref(null);

let buyController = null;
let sellController = null;

const isMyProfile = computed(() => {
  return userStore.userInfo?.uid === props.userInfo?.uid;
});
const isFirstBuy = computed(() => totalOwnerKeysBought.value === 0);
const buyText = computed(() => `BUY ${sliderAmount.value}`);
const sellText = computed(() => `SELL ${sliderAmount.value}`);
const buyEnabled = computed(() => !buyLoading.value && !buyError.value);
const sellEnabled = computed(
  () => !sellLoading.value && !sellError.value && !isFirstBuy.value
);

const fetchBuyCost = async (amount) => {
  // Cancel previous request
  if (buyController) {
    buyController.abort();
  }

  if (!amount || parseInt(amount) <= 0) {
    buyCost.value = "0.0";
    buyLoading.value = false;
    buyError.value = null;
    return;
  }

  buyController = new AbortController();
  buyLoading.value = true;
  buyError.value = null;

  try {
    const response = await getBuyCost(props.userInfo?.uid, amount.toString());

    if (response?.cost && response.cost !== "0") {
      buyCost.value = formatEtherString(response.cost);
    } else {
      buyCost.value = "0.0";
    }
  } catch (error) {
    if (error.name === "AbortError") return;

    console.error("Get BUY cost failed:", error);
    buyError.value = error.message;
    buyCost.value = "0.0";
  } finally {
    buyLoading.value = false;
  }
};

const fetchSellCost = async (amount) => {
  // Cancel previous request
  if (sellController) {
    sellController.abort();
  }

  if (!amount || parseInt(amount) <= 0) {
    sellCost.value = "0.0";
    sellLoading.value = false;
    sellError.value = null;
    return;
  }

  sellController = new AbortController();
  sellLoading.value = true;
  sellError.value = null;

  try {
    const response = await getSellCost(props.userInfo?.uid, amount.toString());

    if (response?.cost && response.cost !== "0") {
      sellCost.value = formatEtherString(response.cost);
    } else {
      sellCost.value = "0.0";
    }
  } catch (error) {
    if (error.name === "AbortError") return;

    console.error("Get SELL cost failed:", error);
    sellError.value = error.message;
    sellCost.value = "0.0";
  } finally {
    sellLoading.value = false;
  }
};

const updateCosts = async (amount) => {
  if (isFirstBuy.value) return;

  const p1 = fetchBuyCost(amount);
  const p2 = fetchSellCost(amount);

  await Promise.all([p1, p2]);
};

const handleBuy = () => {
  if (!buyEnabled.value) return;

  emits("onConfirm", {
    action: "buy",
    amount: sliderAmount.value,
    cost: buyCost.value,
  });

  sendBuyTransaction({
    amount: sliderAmount.value,
    cost: buyCost.value,
  });

  tradeSigilsDialog.value = false;
};

const handleSell = () => {
  if (!sellEnabled.value) return;

  emits("onConfirm", {
    action: "sell",
    amount: sliderAmount.value,
    cost: sellCost.value,
  });

  sendSellTransaction({
    amount: sliderAmount.value,
    cost: sellCost.value,
  });
  tradeSigilsDialog.value = false;
};

const handleFirstBuyView = () => {
  sliderAmount.value = 1;
  buyCost.value = "0.0";
  sellCost.value = "0.0";
};

const openDialog = async () => {
  tradeSigilsDialog.value = true;
  await fetchData();

  await nextTick();

  if (isFirstBuy.value) {
    handleFirstBuyView();
  } else {
    sliderAmount.value = 1;
    updateCosts(1);
  }
};

const closeDialog = () => {
  emits("onCancel");
  tradeSigilsDialog.value = false;
};

const fetchData = async () => {
  loading.value = true;
  try {
    console.log("Fetching total keys bought for user:", props.userInfo);
    const { totalKeysBought } = await getSubjectSummary(props.userInfo?.uid);
    console.log("Total Sigils Bought:", totalKeysBought);
    totalOwnerKeysBought.value = totalKeysBought;
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    loading.value = false;
  }
};

const sendBuyTransaction = async ({ amount, cost }) => {
  loading.value = true;

  try {
    const userWalletAddress = isMyProfile.value
      ? userStore.evmAddress
      : props.userInfo.wallet_address;

    const iface = new ethers.Interface(BattlePoolABI);
    const calldata = iface.encodeFunctionData("buyVouchers", [
      userWalletAddress, // userWalletAddress
      amount.toString(), // amount
    ]);

    const transactionCost = parseFloat(cost);
    let value = "0x0";

    if (transactionCost > 0.0) {
      const weiValue = ethers.parseEther(cost); // Convert AVAX to Wei
      value = ethers.toBeHex(weiValue);
    }

    const tx = {
      from: userStore.evmAddress,
      to: CONTRACT_ADDRESS,
      data: calldata,
      value: value,
    };

    console.log("🚀 Sending BUY transaction:", {
      function: "buyVouchers",
      params: [userWalletAddress, amount.toString()],
      value: cost,
      tx,
    });

    const result = await ParticleBridge.sendTransaction(tx);

    console.log("✅ Buy transaction successful:", result);
    const txHash = resolveTransactionHash(result);

    logFirebaseEvent("buy_sigil", {
      user_id: userStore.userInfo?.uid,
      amount,
      cost,
      tx_hash: txHash,
    });

    dispatchTxAlert({
      action: "Sigils",
      summary: `Vouchers bought${amount ? ` (${amount})` : ""}.`,
      txHash,
      type: "success",
    });

    return {
      transactionHash: txHash,
      amount,
      cost,
    };
  } catch (err) {
    console.error("❌ Buy transaction failed:", err);
    const failureTxHash = resolveTransactionHash(
      err?.receipt,
      err?.error,
      err?.originalError,
      err
    );
    const failureMessage = err?.errorMessage || err?.message || "Unknown error";
    dispatchTxAlert({
      action: "Sigils",
      summary: "Buy vouchers failed.",
      errorMessage: failureMessage,
      txHash: failureTxHash,
      type: "error",
    });
  } finally {
    loading.value = false;
  }
};

const sendSellTransaction = async ({ amount, cost }) => {
  loading.value = true;

  try {
    const userWalletAddress = isMyProfile.value
      ? userStore.evmAddress
      : props.userInfo.wallet_address;

    const iface = new ethers.Interface(BattlePoolABI);
    const calldata = iface.encodeFunctionData("sellVouchers", [
      userWalletAddress,
      amount.toString(),
    ]);

    const tx = {
      from: userStore.evmAddress,
      to: CONTRACT_ADDRESS,
      data: calldata,
      value: "0x0",
    };

    console.log("🚀 Sending SELL transaction:", {
      function: "sellVouchers",
      params: [userWalletAddress, amount.toString()],
      tx,
    });

    // 3. Send via ParticleBridge
    const result = await ParticleBridge.sendTransaction(tx);

    console.log("✅ Sell transaction successful:", result);
    const txHash = resolveTransactionHash(result);

    dispatchTxAlert({
      action: "Sigils",
      summary: `Vouchers sold${amount ? ` (${amount})` : ""}.`,
      txHash,
      type: "success",
    });
    logFirebaseEvent("sell_sigil", {
      user_id: userStore.userInfo?.uid,
      amount,
      cost,
      tx_hash: txHash,
    });
    return {
      transactionHash: txHash,
      amount,
      cost,
    };
  } catch (err) {
    console.error("❌ Sell transaction failed:", err);
    const failureTxHash = resolveTransactionHash(
      err?.receipt,
      err?.error,
      err?.originalError,
      err
    );
    const failureMessage = err?.errorMessage || err?.message || "Unknown error";
    dispatchTxAlert({
      action: "Sigils",
      summary: "Sell vouchers failed.",
      errorMessage: failureMessage,
      txHash: failureTxHash,
      type: "error",
    });
    throw err;
  } finally {
    loading.value = false;
  }
};
// Watch slider changes
watch(sliderAmount, (newAmount) => {
  if (!isFirstBuy.value) {
    updateCosts(newAmount);
  }
});
defineExpose({ openDialog, tradeSigilsDialog, closeDialog });
</script>
<template>
  <v-dialog
    v-model="tradeSigilsDialog"
    class="tw-rounded-2xl !tw-z-[2010]"
    width="400"
    persistent
    scrim="#000"
    opacity="0.5"
  >
    <dialog-close-btn
      :icon="'mdi-close-circle-outline'"
      :iconSize="'32'"
      @onSubmitButton="closeDialog"
    />

    <v-card
      class="!tw-rounded-2xl tw-px-4 tw-py-2"
      min-height="196"
      :loading="loading"
    >
      <v-card-title class="tw-text-center tw-p-0 tw-text-base tw-font-bold">
        Trade Sigils
      </v-card-title>
      <v-card-text
        class="tw-text-center !tw-px-10 !tw-text-sm tw-flex tw-flex-col tw-gap-5"
      >
        <template v-if="isFirstBuy">
          <div>Buy the first Sigil<br />(AVAX) needed for Gas</div>
        </template>
        <div v-else>Slide to Trade Sigils</div>
        <div>
          <v-progress-linear
            v-model="sliderAmount"
            color="#0CB1C7"
            height="25"
            :min="1"
            :max="maxAmount"
          >
            <strong>{{ sliderAmount }} / {{ maxAmount }}</strong>
          </v-progress-linear>
        </div>
        <div class="tw-relative">
          <v-slider
            v-model="sliderAmount"
            color="#0CB1C7"
            :min="1"
            :max="maxAmount"
            :step="1"
            track-color="grey"
            :thumb-label="false"
            thumb-size="10"
            track-size="3"
            :disabled="isFirstBuy"
          />
        </div>
        <div class="tw-grid tw-gap-4 tw-grid-cols-2">
          <!-- Buy Card -->
          <v-sheet
            class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-[#292929] tw-rounded-2xl"
          >
            <div class="tw-uppercase tw-py-2">{{ buyText }}</div>
            <v-btn class="btn-buy" :disabled="!buyEnabled" @click="handleBuy">
              <div class="btn-buy-price">
                <!-- Loading spinner -->
                <v-progress-circular
                  v-if="buyLoading"
                  indeterminate
                  size="20"
                  color="white"
                />

                <!-- Cost display -->
                <template v-else>
                  <Icon
                    icon="cryptocurrency-color:avax"
                    width="32"
                    class="tw-mr-1"
                  />
                  <span>{{ buyCost }}</span> AVAX
                </template>
              </div>
            </v-btn>
          </v-sheet>
          <!-- Sell Card -->
          <v-sheet
            class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-[#292929] tw-rounded-2xl"
            :class="{ 'tw-opacity-50': isFirstBuy }"
          >
            <div class="tw-uppercase tw-py-2">{{ sellText }}</div>
            <v-btn
              class="btn-sell"
              :disabled="!sellEnabled"
              @click="handleSell"
            >
              <div class="btn-sell-price">
                <!-- Loading spinner -->
                <v-progress-circular
                  v-if="sellLoading && !isFirstBuy"
                  indeterminate
                  size="20"
                  color="white"
                />

                <!-- Cost display -->
                <template v-else>
                  <Icon
                    icon="cryptocurrency-color:avax"
                    width="32"
                    class="tw-mr-1"
                  />
                  <span>{{ isFirstBuy ? "0.0" : sellCost }}</span> AVAX
                </template>
              </div>
            </v-btn>
          </v-sheet>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="postcss">
.slider-amount-details {
  @apply tw-absolute tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2;
}

.v-btn {
  &.btn-buy {
    @apply tw-rounded-2xl !tw-h-auto tw-flex-auto;
    background: #47d632 !important;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
    backdrop-filter: blur(10px);
    width: 100%;
    &:before {
      content: "";
      position: absolute;
      top: 10px;
      left: 10px;
      right: 10px;
      bottom: 10px;
      border-radius: inherit;
      background: rgba(0, 0, 0, 0.15);
    }
    .btn-buy-price {
      @apply tw-p-4 tw-flex tw-items-center tw-justify-center tw-flex-wrap;
      span {
        @apply tw-font-bold;
      }
    }
  }

  &.btn-sell {
    @apply tw-rounded-2xl !tw-h-auto tw-flex-auto;
    background: #d64f32 !important;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
    backdrop-filter: blur(10px);
    width: 100%;
    &:before {
      content: "";
      position: absolute;
      top: 10px;
      left: 10px;
      right: 10px;
      bottom: 10px;
      border-radius: inherit;
      background: rgba(0, 0, 0, 0.15);
    }
    .btn-sell-price {
      @apply tw-p-4 tw-flex tw-items-center tw-justify-center tw-flex-wrap;
      span {
        @apply tw-font-bold;
      }
    }
  }
}
</style>
